import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import Yup from '../../utils/yup';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useUsers from '../../context/UsersContext';
import useMenus from '../../context/MenuContext';
import masterDataService from '../../service/masterData.service';
import { generateAutocompleteItems } from '../../utils/helper';
import configService from '../../service/config.service';
import { FormButtonPanelUI, FormHeaderUI } from '../Interface';
import ConfigTabs from './ConfigTabs';
import useConfig from '../../context/ConfigContext';
import { QuantityUnitGroup } from '../../config/quantityUnit';
import { CategoryOptionType, SubjectOptionValues } from '../../config/vatCodes';
import { IssueMethod } from '../../config/production';
import { ItemCodeGenerationMethod } from '../../config/config';

export const ConfigPage = () => {
  const { setConfig } = useConfig();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [formData, setFormData] = useState({});
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    country: [],
    currency: [],
    warehouse: [],
    qtyUnit: [],
    priceList: [],
    paymentMethod: [],
    purchaseVatCode: [],
    salesVatCode: [],
    timeQtyUnit: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  const def = {
    name: '',
    shortName: '',
    nameInPrint: '',
    countryCode: '',
    province: '',
    postCode: '',
    city: '',
    streetAddress: '',
    website: '',
    email: '',
    phoneNumber: '',
    GLN: '',
    hunTaxNumber: '',
    euTaxNumber: '',
    decimalPlaces: '',
    currencyCode: '',
    defaultWarehouse: '',
    defOperationQtyUnit: '',
    defaultDependence: '',
    other: '',
    defCompanyShippingAddr: '',
    defCompanyBillingAddr: '',
    isManufacturedItemInsertable: true,
    operationSequence: false,
    onlyGroupMachine: false,
    productionAllowCompletedToApproved: true,
    defPriceList: '',
    defPaymentMethod: '',
    defTaskKanbanOrder: 'UPDATEDAT',
    defProjectKanbanOrder: '',
    defFeedbackKanbanOrder: 'VISORDER',
    defResourceKanbanOrder: '',
    defServiceOperationQtyUnit: '',
    defTaskDuration: null,
    defTaskFromTime: null,
    defTaskToTime: null,
    conformityStandard: '',
    numberOfDaysExchangeRate: '',
    lastExchangeRateLoaded: null,
    generateProjWorkingNumber: false,
    purchaseVatCodeItem: null,
    purchaseVatCodeService: null,
    salesVatCodeItem: null,
    salesVatCodeService: null,
    defTaskListReportQtyUnit: null,
    defTaskListReportTimeSpent: null,
    defTaskListReportFromTime: null,
    defTaskListReportToTime: null,
    materialDefWhs: null,
    productDefWhs: null,
    productDefBin: null,
    productionDefIssueMethod: null,
    defCompanyCountry: null,
    sumFloatingPoint: true,
    priceFloatingPoint: true,
    quantityFloatingPoint: true,
    defTaskQtyUnit: null,
    itemCodeGenerationMethod: ItemCodeGenerationMethod.LEGACY,
    issueToProdReg: false,
    emailSendingEnabled: false,
    smtpHost: '',
    smtpPort: null,
    smtpEncryption: '',
    smtpUsername: '',
    smtpPassword: '',
    emailSenderName: '',
    emailSenderAddress: '',
    defaultReplyToAddress: '',
    emailSendCopyToSender: false,
    smsSendingEnabled: false,
    smsProviderUrl: '',
    smsApiKey: '',
    smsSender: '',
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();

    setFormData(def);
    initDefaultValues();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('config');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (!isLoading) {
      getConfig();
    }
  }, [isLoading]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    shortName: Yup.string().nullable(),
    nameInPrint: Yup.string().nullable(),
    countryCode: Yup.string().nullable(),
    province: Yup.string().nullable(),
    postCode: Yup.string().nullable(),
    city: Yup.string().nullable(),
    streetAddress: Yup.string().nullable(),
    website: Yup.string().nullable(),
    email: Yup.string().email().nullable(),
    phoneNumber: Yup.string().nullable(),
    GLN: Yup.string().nullable(),
    hunTaxNumber: Yup.string().nullable(),
    euTaxNumber: Yup.string().nullable(),
    decimalPlaces: Yup.number().nullable().max(5),
    currencyCode: Yup.string().nullable(),
    defaultWarehouse: Yup.string().nullable(),
    defOperationQtyUnit: Yup.string().nullable(),
    defaultDependence: Yup.string().nullable(),
    other: Yup.string().nullable(),
    defCompanyShippingAddr: Yup.string().nullable(),
    defCompanyBillingAddr: Yup.string().nullable(),
    isManufacturedItemInsertable: Yup.boolean(),
    operationSequence: Yup.boolean(),
    onlyGroupMachine: Yup.boolean(),
    productionAllowCompletedToApproved: Yup.boolean(),
    defPriceList: Yup.string().nullable(),
    defPaymentMethod: Yup.string().nullable(),
    defTaskKanbanOrder: Yup.string().nullable(),
    defProjectKanbanOrder: Yup.string().nullable(),
    defFeedbackKanbanOrder: Yup.string().nullable(),
    defResourceKanbanOrder: Yup.string().nullable(),
    defServiceOperationQtyUnit: Yup.string().nullable(),
    defTaskDuration: Yup.number().nullable().positive(),
    defTaskFromTime: Yup.string().nullable(),
    defTaskToTime: Yup.string().nullable(),
    conformityStandard: Yup.string().nullable(),
    numberOfDaysExchangeRate: Yup.number().nullable().integer().min(0),
    generateProjWorkingNumber: Yup.boolean(),
    purchaseVatCodeItem: Yup.string().nullable(),
    purchaseVatCodeService: Yup.string().nullable(),
    salesVatCodeItem: Yup.string().nullable(),
    salesVatCodeService: Yup.string().nullable(),
    defTaskListReportQtyUnit: Yup.string().nullable(),
    defTaskListReportTimeSpent: Yup.number().nullable(),
    defTaskListReportFromTime: Yup.string().nullable(),
    defTaskListReportToTime: Yup.string().nullable(),
    materialDefWhs: Yup.string().nullable(),
    productDefWhs: Yup.string().nullable(),
    productDefBin: Yup.number().nullable(),
    productionDefIssueMethod: Yup.string().nullable(),
    defCompanyCountry: Yup.string().nullable(),
    sumFloatingPoint: Yup.boolean(),
    priceFloatingPoint: Yup.boolean(),
    quantityFloatingPoint: Yup.boolean(),
    defTaskQtyUnit: Yup.string().nullable(),
    itemCodeGenerationMethod: Yup.string().nullable(),
    issueToProdReg: Yup.boolean(),
    emailSendingEnabled: Yup.boolean(),
    smtpHost: Yup.string().nullable(),
    smtpPort: Yup.number().nullable().integer().min(1).max(65535),
    smtpEncryption: Yup.string().nullable(),
    smtpUsername: Yup.string().nullable(),
    smtpPassword: Yup.string().nullable(),
    emailSenderName: Yup.string().nullable(),
    emailSenderAddress: Yup.string().email().nullable(),
    defaultReplyToAddress: Yup.string().email().nullable(),
    emailSendCopyToSender: Yup.boolean(),
    smsSendingEnabled: Yup.boolean(),
    smsProviderUrl: Yup.string().trim().url().nullable().when('smsSendingEnabled', {
      is: true,
      then: (schema) => schema.required(),
    }),
    smsApiKey: Yup.string().trim().nullable().when('smsSendingEnabled', {
      is: true,
      then: (schema) => schema.required(),
    }),
    smsSender: Yup.string().trim().nullable().when('smsSendingEnabled', {
      is: true,
      then: (schema) => schema.required(),
    }),
  });

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .countryToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .currencyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .warehouseToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .quantityUnitToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .priceListToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .paymentMethodToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .vatCodeToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          country: resp[0],
          currency: resp[1],
          warehouse: resp[2],
          qtyUnit: resp[3],
          priceList: resp[4],
          paymentMethod: resp[5],
          vatCode: resp[6],
        };

        const filteredQtyUnit = aData?.qtyUnit?.filter((opQtyUnit) => opQtyUnit.group === QuantityUnitGroup.TIME);
        const filteredPurchaseVatCodeItem = aData?.vatCode?.filter((vc) => {
          return (
            vc.category === CategoryOptionType.B &&
            (vc.subject === SubjectOptionValues.T || vc.subject === SubjectOptionValues.TS)
          );
        });
        const filteredPurchaseVatCodeService = aData?.vatCode?.filter(
          (vc) =>
            vc.category === CategoryOptionType.B &&
            (vc.subject === SubjectOptionValues.S || vc.subject === SubjectOptionValues.TS)
        );
        const filteredSalesVatCodeItem = aData?.vatCode?.filter(
          (vc) =>
            vc.category === CategoryOptionType.K &&
            (vc.subject === SubjectOptionValues.T || vc.subject === SubjectOptionValues.TS)
        );
        const filteredSalesVatCodeService = aData?.vatCode?.filter(
          (vc) =>
            vc.category === CategoryOptionType.K &&
            (vc.subject === SubjectOptionValues.S || vc.subject === SubjectOptionValues.TS)
        );

        const acOptions = {
          country: generateAutocompleteItems(aData.country, 'name', 'code'),
          currency: generateAutocompleteItems(aData.currency, 'code', 'code'),
          warehouse: generateAutocompleteItems(aData.warehouse, 'code', 'code'),
          priceList: generateAutocompleteItems(aData.priceList, 'name', 'id'),
          paymentMethod: generateAutocompleteItems(aData.paymentMethod, 'name', 'id'),
          purchaseVatCodeItem: generateAutocompleteItems(filteredPurchaseVatCodeItem, ['code', 'name'], 'code'),
          purchaseVatCodeService: generateAutocompleteItems(filteredPurchaseVatCodeService, ['code', 'name'], 'code'),
          salesVatCodeItem: generateAutocompleteItems(filteredSalesVatCodeItem, ['code', 'name'], 'code'),
          salesVatCodeService: generateAutocompleteItems(filteredSalesVatCodeService, ['code', 'name'], 'code'),
          timeQtyUnit: generateAutocompleteItems(filteredQtyUnit, 'code', 'id'),
        };
        setAutoCompleteOptions(acOptions);
        setIsLoading(false);
      })
      .finally(() => {
        //hideLoader();
      });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...values,
      decimalPlaces: parseInt(values.decimalPlaces),
      defTaskDuration: parseFloat(values.defTaskDuration),
      defTaskListReportTimeSpent: parseFloat(values.defTaskListReportTimeSpent),
      sumFloatingPoint: values?.sumFloatingPoint ?? true,
      priceFloatingPoint: values?.priceFloatingPoint ?? true,
      quantityFloatingPoint: values?.quantityFloatingPoint ?? true,
      smtpPort: values.smtpPort === '' || values.smtpPort === null ? null : parseInt(values.smtpPort),
    };

    configService
      .rowData(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        setConfig(data);
        setFormData(data);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const getConfig = () => {
    configService.getData().then((data) => {
      setFormData({
        ...data,
        productionAllowCompletedToApproved: data?.productionAllowCompletedToApproved ?? true,
        onlyGroupMachine: data?.onlyGroupMachine ?? false,
        sumFloatingPoint: data?.sumFloatingPoint ?? true,
        priceFloatingPoint: data?.priceFloatingPoint ?? true,
        quantityFloatingPoint: data?.quantityFloatingPoint ?? true,
        itemCodeGenerationMethod: data?.itemCodeGenerationMethod ?? ItemCodeGenerationMethod.LEGACY,
        emailSendingEnabled: data?.emailSendingEnabled ?? false,
        emailSendCopyToSender: data?.emailSendCopyToSender ?? false,
        smsSendingEnabled: data?.smsSendingEnabled ?? false,
        smsProviderUrl: data?.smsProviderUrl ?? '',
        smsApiKey: data?.smsApiKey ?? '',
        smsSender: data?.smsSender ?? '',
      });
    });
  };

  return (
    <div className="custom-scrollbar">
      <FormHeaderUI
        modClick={() => {
          setViewField(false);
          setEditButtonvisible(false);
        }}
        modDisabled={!viewField || !canEdit}
      />

      <Formik
        initialValues={formData}
        validationSchema={validationSchema}
        validateOnChange={false}
        onSubmit={handleInsertObject}
        enableReinitialize
      >
        {({ resetForm }) => (
          <Form>
            <ConfigTabs viewField={viewField} formData={formData} autoCompleteOptions={autoCompleteOptions} />

            <FormButtonPanelUI
              onCancelClick={() => {
                setViewField(true);
                setEditButtonvisible(true);

                resetForm();
              }}
              disabled={editButtonVisible}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};
